import React from "react";
import { Flex } from "@jsq/ds/core";
import { type Money as MoneyType } from "common/types/mainTypes";
import { formatMoney } from "./utils";
import { DEFAULT_BASE_MONEY_PROPS } from "./constants";

export type Props = {
  money: MoneyType | undefined | null;
  format?: MoneyFormat;
  rounded?: boolean; // whether to round to the nearest whole number
  suffix?: string;
  dashesAsDefault?: boolean;
  precision?: number;
  entityLabel?: string | null;
};

// how to format a monetary amount as a string
export enum MoneyFormat {
  Symbol = "symbol", // currency symbol only
  SymbolAndCode = "symbol_and_currency_code", // with currency symbol and code
  SummaryWithCode = "summary_with_currency_code", // summary with currency code ie. USD 1T
  SummaryWithSymbol = "summary_with_symbol", // if < 1M: $999,999 else: $1.23M, $1.45B, $1.67T
  Summary = "summary", // 123.4 (b|m)illion if in the (b|m)illions, 123.4 thousand if in the hundreds of thousands
  Amount = "amount", // the amount only, ignores the currency symbol
}

export const Money: React.FunctionComponent<Props> = ({
  money = DEFAULT_BASE_MONEY_PROPS,
  format = MoneyFormat.Symbol,
  rounded = true,
  precision = undefined,
  suffix = "",
  dashesAsDefault = false,
  entityLabel = null,
}) => {
  const formattedMoney = () => {
    if (!money) {
      return dashesAsDefault ? <>--</> : null;
    }
    return (
      <>
        {formatMoney(money, format, rounded, precision)}
        {suffix ? ` ${suffix}` : null}
      </>
    );
  };

  if (entityLabel && money) {
    return (
      <Flex gap={2} justifyContent="flex-end">
        {formattedMoney()}
        <span style={{ whiteSpace: "nowrap" }}>{`(${entityLabel})`}</span>
      </Flex>
    );
  }

  return formattedMoney();
};

export default Money;
